import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import { MENTORS } from '../data/mentors';
import { useToast } from '../components/ui/Toast';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiFeather, FiMic, FiCopy, FiTrash2, FiList, FiGitMerge, FiSun, FiCloud, FiMoon } = FiIcons;

const HERO_IMAGE = "https://cdn.midjourney.com/21f9857f-0f81-4f2c-9c68-cec088f18fed/0_0.webp";
const STORAGE_KEY = 'mindgarden-greenhouse-v1';

const MOODS = [
  { key: 'positive', label: 'Positive', color: '#9BB898', icon: FiSun },
  { key: 'neutral', label: 'Neutral', color: '#6DAEDB', icon: FiCloud },
  { key: 'negative', label: 'Reflective', color: '#B79AD0', icon: FiMoon },
];

export default function TheGreenhouse() {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState({ text: '', mood: 'neutral', mentorKey: 'butler' });
  const [isComposing, setIsComposing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const { success, error } = useToast();

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setNotes(JSON.parse(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
  }, [notes]);

  const handleAddNote = () => {
    if (!newNote.text.trim()) return;
    const noteToAdd = {
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      ...newNote,
    };
    setNotes([noteToAdd, ...notes]);
    setNewNote({ text: '', mood: 'neutral', mentorKey: 'butler' });
    setIsComposing(false);
    success('Thought nurtured in The Greenhouse.');
  };

  const handleDeleteNote = (id) => {
    setNotes(notes.filter(note => note.id !== id));
    success('Note composted.');
  };

  const handleCopyNote = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      success('Thought harvested to clipboard!');
    } catch (err) {
      error('Failed to copy.');
    }
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      error('Voice recognition is not supported in your browser.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = (event) => {
      error(`Voice recognition error: ${event.error}`);
      setIsListening(false);
    };
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setNewNote(prev => ({ ...prev, text: prev.text ? `${prev.text} ${transcript}` : transcript }));
    };

    if (isListening) {
      recognition.stop();
    } else {
      recognition.start();
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative h-48 lg:h-64 rounded-3xl overflow-hidden shadow-xl">
        <div className="absolute inset-0 bg-black/30 z-10" />
        <img 
          src={HERO_IMAGE} 
          alt="The Greenhouse" 
          className="w-full h-full object-cover" 
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 lg:p-8">
          <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-white mb-2 text-shadow-sm">
            The Greenhouse
          </h1>
          <p className="text-white/90 font-dm-sans max-w-lg">
            A protected space for delicate ideas. Nurture your quiet thoughts until they are strong enough to grow wild.
          </p>
        </div>
      </div>

      <Card className="p-4 bg-white/80 backdrop-blur-md border-sage/20">
        {!isComposing ? (
          <Button variant="primary" onClick={() => setIsComposing(true)} icon={FiPlus} className="w-full shadow-lg hover:shadow-xl transition-all">
            Nurture a New Thought
          </Button>
        ) : (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-4">
            <textarea
              value={newNote.text}
              onChange={(e) => setNewNote({ ...newNote, text: e.target.value })}
              placeholder="Plant an idea, a reflection, or an experiment..."
              className="w-full p-4 border border-sage/20 rounded-xl bg-white focus:ring-2 focus:ring-sage/50 min-h-[120px] resize-none font-dm-sans text-lg"
              autoFocus
            />
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {MOODS.map(mood => (
                  <button
                    key={mood.key}
                    title={mood.label}
                    onClick={() => setNewNote({ ...newNote, mood: mood.key })}
                    className={`w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center ${newNote.mood === mood.key ? 'border-charcoal scale-110 shadow-md' : 'border-transparent hover:bg-gray-100'}`}
                    style={{ backgroundColor: newNote.mood === mood.key ? mood.color : 'transparent' }}
                  >
                    <SafeIcon icon={mood.icon} className={`w-5 h-5 ${newNote.mood === mood.key ? 'text-white' : 'text-charcoal/60'}`} />
                  </button>
                ))}
              </div>
              
              <div className="flex items-center gap-2">
                <select
                  value={newNote.mentorKey}
                  onChange={(e) => setNewNote({ ...newNote, mentorKey: e.target.value })}
                  className="p-2.5 border border-sage/20 rounded-xl bg-white text-charcoal font-dm-sans focus:outline-none focus:ring-2 focus:ring-sage/50 text-sm"
                >
                  {Object.values(MENTORS).map(mentor => (
                    <option key={mentor.key} value={mentor.key}>
                      {mentor.name}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleVoiceInput}
                  className={`!p-2.5 ${isListening ? 'bg-crimson/10 text-crimson border-crimson/20 animate-pulse' : ''}`}
                  title="Water your thought (voice input)"
                >
                  <SafeIcon icon={FiMic} className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-sage/10">
              <Button variant="secondary" onClick={() => setIsComposing(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleAddNote} disabled={!newNote.text.trim()}>
                Plant Seed
              </Button>
            </div>
          </motion.div>
        )}
      </Card>

      <div className="flex items-center justify-between px-2">
        <h2 className="text-xl font-cormorant font-bold text-charcoal flex items-center gap-2">
          <SafeIcon icon={FiList} className="w-5 h-5 text-sage" />
          Growing Thoughts
        </h2>
        <div className="flex gap-2">
          <span className="text-xs font-bold text-charcoal/40 uppercase tracking-wider bg-sage/10 px-2 py-1 rounded-md">
            {notes.length} Seeds
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AnimatePresence>
          {notes.map(note => (
            <NoteCard key={note.id} note={note} onCopy={handleCopyNote} onDelete={handleDeleteNote} />
          ))}
        </AnimatePresence>
        
        {notes.length === 0 && (
          <div className="col-span-full py-16 text-center border-2 border-dashed border-sage/20 rounded-3xl bg-sage/5">
            <SafeIcon icon={FiFeather} className="w-12 h-12 text-sage/40 mx-auto mb-4" />
            <h3 className="text-lg font-cormorant font-bold text-charcoal mb-1">Your greenhouse is empty.</h3>
            <p className="text-charcoal/60 font-dm-sans">Start by nurturing a new thought above.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function NoteCard({ note, onCopy, onDelete }) {
  const mood = MOODS.find(m => m.key === note.mood) || MOODS[1];
  const mentor = MENTORS[note.mentorKey];
  const hasLongText = note.text.length > 200;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="h-full"
    >
      <Card className="h-full flex flex-col p-5 hover:shadow-lg transition-shadow duration-300 border-l-4" style={{ borderLeftColor: mood.color }}>
        <p className="text-charcoal/80 font-dm-sans leading-relaxed whitespace-pre-wrap flex-grow text-sm lg:text-base">
          {note.text}
        </p>
        
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-sage/10">
          <div className="flex items-center gap-3">
            {mentor && (
              <div className="flex items-center gap-1.5 text-xs font-bold text-charcoal/60 bg-gray-100 px-2 py-1 rounded-md">
                <SafeIcon icon={mentor.icon} className="w-3 h-3" />
                <span>{mentor.name}</span>
              </div>
            )}
            <span className="text-xs text-charcoal/40">
              {new Date(note.createdAt).toLocaleDateString()}
            </span>
          </div>
          
          <div className="flex items-center gap-1">
            <button 
              onClick={() => onCopy(note.text)} 
              className="p-2 text-charcoal/40 hover:text-sage hover:bg-sage/10 rounded-lg transition-colors"
              title="Harvest to Clipboard"
            >
              <SafeIcon icon={FiCopy} className="w-4 h-4" />
            </button>
            <button 
              onClick={() => onDelete(note.id)} 
              className="p-2 text-charcoal/40 hover:text-crimson hover:bg-crimson/10 rounded-lg transition-colors"
              title="Compost Note"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}